// src/lib/direct-preview-engine.js — PARTIE B / B5 (ADR-272) : moteur d'APERÇU
// Direct côté CLIENT (bundle CRA), miroir strict du moteur riche SERVEUR
// lib/direct-rich-render.js.
//
// POURQUOI CE FICHIER — B5 rend l'aperçu de l'éditeur Direct INTERACTIF : des
// cases à cocher simulent l'état d'une réservation (formulaire rempli, caution
// reçue, empreinte vs débit, remboursement, motifs…) et l'aperçu se recalcule
// EN DIRECT. Pour que « ce que Dimitri voit » soit « ce que le voyageur
// recevra », l'aperçu doit rejouer EXACTEMENT les deux passes du serveur :
//
//   1. passe CONDITIONNELLE : evaluateConditions(text, {vars}, directConditionResolver)
//      → garde/masque les blocs {#if …} (+ cumul ET / OU) selon le contexte.
//   2. passe INTERPOLATION   : {token} → valeur du contexte.
//
// ⚠️ DUPLICATION CONTRÔLÉE (ADR-021, comme template-variables / caution-mode /
// payment-derivation) : CRA (ModuleScopePlugin) interdit d'importer la racine
// lib/ depuis src/. Ce module est donc le JUMEAU ESM de lib/direct-rich-render.js.
// Toute modification du résolveur ou de l'ordre des passes DOIT être répliquée
// aux DEUX fichiers. La parité est verrouillée par
// src/lib/direct-preview-parity.test.js (BLOQUANT CI).
//
// ⚠️ ZÉRO ÉCRITURE, ZÉRO ENVOI : ce module est PUR (aucun réseau, aucun état).
// Il ne sert QUE l'aperçu admin — le contenu stocké garde toujours ses {tokens}
// et ses blocs {#if …}.
//
// ⚠️ LIMITE ASSUMÉE (règle « texte d'aide = état réel ») : le serveur pré-expand
// les fragments partagés `{{frag:KEY}}` AVANT le rendu riche ; l'aperçu admin ne
// le fait pas → un `{{frag:KEY}}` s'affiche LITTÉRALEMENT dans l'aperçu. C'est
// dit noir sur blanc dans le pied du panneau de scénario.

import { evaluateConditions } from "./template-variables";

/**
 * Résolveur de CONTEXTE DIRECT — miroir byte-parallèle de
 * `directConditionResolver` (lib/direct-rich-render.js).
 *
 * Signature identique à `resolveValue(name, ctx)` attendue par evalIfExpression :
 * renvoie une valeur (vraie si non-vide après trim) ou null (« inconnue » →
 * fausse → bloc masqué, fail-safe).
 *
 * @param {string} name nom de variable/drapeau
 * @param {{vars?: object}} ctx contexte d'aperçu
 * @returns {string|null}
 */
export function directConditionResolver(name, ctx) {
  const vars = (ctx && ctx.vars) || {};
  if (Object.prototype.hasOwnProperty.call(vars, name)) {
    const v = vars[name];
    return v == null ? "" : String(v);
  }
  return null; // variable hors contexte Direct → fausse (fail-safe)
}

/**
 * Interpolation d'aperçu (client) : remplace {token} par sa valeur d'exemple si
 * connue, sinon laisse le {token} LITTÉRAL. Déplacé TEL QUEL depuis
 * DirectMessagesView.jsx (B4) — comportement byte-identique, aucun changement.
 *
 * @param {string} text
 * @param {object} vars
 * @returns {string}
 */
export function interpolatePreview(text, vars) {
  if (typeof text !== "string") return "";
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, tok) => {
    const v = vars[tok];
    return v == null ? whole : String(v);
  });
}

/**
 * Rend un texte Direct pour l'APERÇU : passe conditionnelle {#if …} PUIS
 * interpolation {token}. Miroir de `renderDirectRich(template, vars, interpolate)`.
 *
 * ⚠️ Appliqué à l'OBJET **et** au CORPS — le serveur fait de même
 * (lib/journey-templates.js applique `renderBody` à subject_fr comme à body_fr).
 * C'est une CORRECTION DE PARITÉ par rapport à B4 (où l'objet n'était
 * qu'interpolé) : un objet portant un {#if} littéral est désormais évalué comme
 * il le sera à l'envoi.
 *
 * @param {string} template texte du gabarit (avec {token} et blocs {#if …})
 * @param {object} vars contexte d'aperçu (exemple + scénario + drapeaux)
 * @returns {string}
 */
export function renderDirectPreview(template, vars) {
  if (typeof template !== "string") return "";
  const ctx = { vars: vars || {} };
  const conditioned = evaluateConditions(
    template,
    ctx,
    directConditionResolver,
  );
  return interpolatePreview(conditioned, vars);
}
